/* Imports */
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
} from '@apollo/client/core';
import 'string_score';
import mapSearchQuery from '../../graphqlServer/queries/mapSearchQuery.js';
import lakesSearchQuery from '../../graphqlServer/queries/lakesSearchQuery.js';
import promotedPricingQuery from '../../graphqlServer/queries/promotedPricingQuery.js';
import lakeByNameSearchQuery from '../../graphqlServer/queries/lakeByNameSearchQuery.js';
import { getDistanceFromLatLong } from '../helpers/mainHelpers.js';
import { flow } from '../helpers/flow.js';
import {
  fetchMapCards, fetchLakesByLatLong, fetchLakesByName, fetchPromotedPricing,
} from '../helpers/apiClient.js';

export const sortMaps = (maps) => maps.sort((a, b) => {
  const difference = a.sortPrice - b.sortPrice;
  if (difference === 0) {
    if (a.name > b.name) return 1;
    if (a.name < b.name) return -1;
    return 0;
  }
  return difference;
});

const prepareMaps = flow(sortMaps);

export default {
  state: {
    searchQuery: '',
    formattedAddress: 'Lebanon, KS 66952, USA', // Default map location
    isError: false,
    noResults: false,
    isLoading: false,
    maps: [],
    lakes: [],
    lakesByRelevance: [],
    activeLake: null,
    showLakeInfo: false,
    showLakeFeatures: false,
    searchType: 'location',
    currencyCode: window?.AppData?.currencyCode || 'NA',
  },
  getters: {
    getSearchQuery: (currentState) => currentState.searchQuery,
    getFormattedAddress: (currentState) => currentState.formattedAddress,
    getMaps: (currentState) => currentState.maps,
    getLakes: (currentState) => currentState.lakes,
    getActiveLake: (currentState) => currentState.activeLake,
    getIsError: (currentState) => currentState.isError,
    getNoResults: (currentState) => currentState.noResults,
    getIsLoading: (currentState) => currentState.isLoading,
    getShowLakeInfo: (currentState) => currentState.showLakeInfo,
    getShowLakeFeatures: (currentState) => currentState.showLakeFeatures,
    getLakesByRelevance: (currentState) => currentState.lakesByRelevance,
    getSearchType: (currentState) => currentState.searchType,
    // NOTE: currentState is left on purpose even though it's not used -> it breaks reactivity if removed :-)
    getIsGarminMap: (currentState, rootGetters) => {
      const {
        getActiveBrand: activeBrand,
      } = rootGetters;

      return activeBrand === 'garmin';
    },
    getCurrencyCode: (currentState) => currentState.currencyCode,
  },
  mutations: {
    setSearchQuery(state, searchQuery) {
      state.searchQuery = searchQuery;
    },
    setFormattedAddress(state, formattedAddress) {
      state.formattedAddress = formattedAddress;
    },
    setMaps(state, maps) {
      state.maps = maps;
    },
    setLakes(state, lakes) {
      state.lakes = lakes;
    },
    setActiveLake(state, lake) {
      state.activeLake = lake;
    },
    setIsError(state, isError) {
      state.isError = isError;
    },
    setNoResults(state, noResults) {
      state.noResults = noResults;
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setShowLakeInfo(state, showLakeInfo) {
      state.showLakeInfo = showLakeInfo;
    },
    setShowLakeFeatures(state, payload) {
      state.showLakeFeatures = payload;
    },
    setLakesByRelevance(state, payload) {
      state.lakesByRelevance = payload;
    },
    setSearchType(state, payload) {
      state.searchType = payload;
    },
  },
  actions: {
    setSearchQuery({ commit }, payload) {
      commit('setSearchQuery', payload);
    },
    setFormattedAddress({ commit }, payload) {
      commit('setFormattedAddress', payload);
    },
    /**
     * Searches for map cards and lakes at the current map location, then
     * applies promoted pricing. Uses either the GraphQL client or the REST
     * API client depending on the useGraphQL flag.
     * @param {object} vuexContext - The Vuex action context.
     * @param {function(string, unknown): void} vuexContext.commit - Commits a mutation.
     * @param {object} vuexContext.rootState - The root store state.
     * @param {object} vuexContext.rootGetters - The root store getters.
     * @param {object} vuexContext.state - This module's state.
     * @param {function(string, unknown): Promise} vuexContext.dispatch - Dispatches an action.
     * @returns {Promise<void>} Resolves when search state has been updated.
     */
    async searchMap({
      commit,
      rootState,
      rootGetters,
      state,
      dispatch,
    }) {
      await rootGetters;
      const {
        getGraphQLEndpoint: graphQLEndpoint,
        getIsGarminMap: isGarminMap,
        getUseGraphQL: useGraphQL,
      } = rootGetters;

      const { latLng } = rootState.map;
      const { lat, lng } = latLng;
      const locale = window?.location?.pathname.split('/')[1];

      commit('setNoResults', false);
      commit('setIsLoading', true);
      commit('setIsError', false);

      try {
        let mapData;
        let lakesData;

        if (useGraphQL) {
          const cache = new InMemoryCache();
          const client = new ApolloClient({ cache, link: new HttpLink({ uri: graphQLEndpoint }) });

          const [mapsResponse, lakesResponse] = await Promise.allSettled([client.query({
            query: mapSearchQuery,
            variables: {
              lat: lat.toString(),
              long: lng.toString(),
              locale,
              isGarminMap,
            },
          }), client.query({
            query: lakesSearchQuery,
            variables: {
              lat: lat.toString(),
              long: lng.toString(),
              locale,
              isGarminMap,
            },
          })]);

          mapData = mapsResponse?.value?.data?.getMapCardsByLatLong || [];
          lakesData = lakesResponse?.value?.data?.getLakesByLatLong || [];

          // Filter maps that are not valid
          mapData = mapData.filter((map) => !!map.sku);

          // Query promoted pricing
          const promotedPrices = await client.query({
            query: promotedPricingQuery,
            variables: {
              skus: mapData.map((map) => map.priceSku),
              locale,
            },
          });

          if (promotedPrices?.data?.getPromotedPricing) {
            mapData.forEach((map, index) => {
              const promoPrice = promotedPrices?.data?.getPromotedPricing?.find(
                (obj) => obj.sku === map.priceSku,
              );
              if (!promoPrice) {
                return;
              }

              const { formattedListPrice, formattedSalePrice } = promoPrice;

              if (formattedSalePrice === formattedListPrice) {
                return;
              }

              mapData[index] = {
                ...map,
                formattedListPrice,
                formattedSalePrice,
              };
            });
          }
        } else {
          // REST API path
          const [mapsResponse, lakesResponse] = await Promise.allSettled([
            fetchMapCards({
              lat: lat.toString(),
              long: lng.toString(),
              locale,
              isGarminMap,
            }),
            fetchLakesByLatLong({
              lat: lat.toString(),
              long: lng.toString(),
              isGarminMap,
            }),
          ]);

          mapData = mapsResponse?.value || [];
          lakesData = lakesResponse?.value || [];

          // Filter maps that are not valid
          mapData = mapData.filter((map) => !!map.sku);

          // Query promoted pricing
          const promotedPrices = await fetchPromotedPricing({
            skus: mapData.map((map) => map.priceSku),
            locale,
          });

          if (promotedPrices) {
            mapData.forEach((map, index) => {
              const promoPrice = promotedPrices.find(
                (obj) => obj.sku === map.priceSku,
              );
              if (!promoPrice) {
                return;
              }

              const { formattedListPrice, formattedSalePrice } = promoPrice;

              if (formattedSalePrice === formattedListPrice) {
                return;
              }

              mapData[index] = {
                ...map,
                formattedListPrice,
                formattedSalePrice,
              };
            });
          }
        }

        const maps = prepareMaps(mapData);

        let lakeResult;

        if (state.searchQuery) {
          if (state.searchType === 'location') {
            lakeResult = lakesData?.find(
              (lake) => lake.name.toLowerCase() === state.searchQuery.toLowerCase(),
            );
          }

          if (state.searchType === 'lakes') {
            const { lakesByRelevance } = state;
            // Get most relevant lake result
            /* eslint-disable-next-line prefer-destructuring */
            lakeResult = lakesByRelevance[0];
          }

          if (lakeResult) {
            // Center the map onto the specific lake
            this.dispatch('setLatLng', {
              lat: lakeResult.center_lat,
              lng: lakeResult.center_lon,
            });

            // Zoom the map to level 10
            this.dispatch('setZoom', 10);

            // Pre-select the lake and show details
            dispatch('setActiveLake', lakeResult);
            dispatch('setShowLakeInfo', true);
            dispatch('setShowLakeFeatures', true);
          }
        }

        commit('setMaps', maps);
        commit('setLakes', lakesData);
        dispatch('setResultsStates');
        commit('setIsLoading', false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        commit('setIsError', true);
        commit('setIsLoading', false);
      }
    },
    setResultsStates({ commit, state }) {
      const { maps } = state;
      const noResults = !maps.length > 0;

      if (noResults) commit('setNoResults', true);
    },
    setNoResultsStates({ commit }) {
      commit('setNoResults', true);
      commit('setIsLoading', false);
    },
    performSearch({ state, dispatch }, payload) {
      if (state.searchType === 'lakes') {
        return dispatch('lakeSearch', payload).then((searchResults) => {
          if (searchResults.length) {
            const relevantLake = searchResults[0];

            this.dispatch('updateMapInfo', {
              lat: relevantLake.center_lat,
              lng: relevantLake.center_lon,
              bounds: null,
            });
            this.dispatch('setShowLocationMarker', true);
          } else {
            // No results
            dispatch('noResults');
            this.dispatch('setShowLocationMarker', false);
          }
        });
      }

      // call geoencoding
      let lat = 0;
      let lng = 0;
      return dispatch('addressSearch', payload).then(
        (searchResults, status) => {
          if (searchResults) {
            let index = 1;
            if (searchResults.length < 2) {
              index = 0;
            }
            lat = searchResults[index].geometry.location.lat();
            lng = searchResults[index].geometry.location.lng();
            this.dispatch('updateMapInfo', {
              lat,
              lng,
              bounds: searchResults[index].geometry.bounds,
            });
            dispatch(
              'setFormattedAddress',
              searchResults[index].formatted_address,
            );
            this.dispatch('setShowLocationMarker', true);
          } else {
            // no results
            dispatch('noResults');
            dispatch('setFormattedAddress', status);
            this.dispatch('setShowLocationMarker', false);
          }
        },
      );
    },
    addressSearch({ rootGetters }, payload) {
      return new Promise((resolve) => {
        const googleMapsApi = rootGetters.getGoogleMapsApi;
        const geocoder = new googleMapsApi.maps.Geocoder();
        geocoder.geocode(
          { address: decodeURIComponent(payload) },
          (results, status) => {
            if (status === 'OK') {
              resolve(results);
            } else {
              resolve(null);
            }
          },
        );
      });
    },
    /**
     * Searches for lakes by the current search query and stores them sorted
     * by relevance (name score, then distance). Uses either the GraphQL
     * client or the REST API client depending on the useGraphQL flag.
     * @param {object} vuexContext - The Vuex action context.
     * @param {object} vuexContext.rootState - The root store state.
     * @param {object} vuexContext.rootGetters - The root store getters.
     * @param {object} vuexContext.state - This module's state.
     * @param {function(string, unknown): Promise} vuexContext.dispatch - Dispatches an action.
     * @returns {Promise<Array<object>|null>} The lakes sorted by relevance,
     * an empty array on REST fetch failure, or null when no data returned.
     */
    lakeSearch({
      rootState,
      rootGetters,
      state,
      dispatch,
    }) {
      const {
        getGraphQLEndpoint: graphQLEndpoint,
        getIsGarminMap: isGarminMap,
        getUseGraphQL: useGraphQL,
      } = rootGetters;

      const { latLng } = rootState.map;
      const { lat, lng } = latLng;

      let lakesByRelevance;

      const processLakesData = (lakesData) => {
        const setDistance = (lakeData) => {
          const calculatedDistance = getDistanceFromLatLong(
            lat,
            lng,
            lakeData.center_lat,
            lakeData.center_lon,
          );
          return { ...lakeData, distance: calculatedDistance };
        };

        const setScore = (lakeData) => {
          const calculatedScore = lakeData.name.score(state.searchQuery);
          return { ...lakeData, score: calculatedScore };
        };

        const sortByRelevance = (locationA, locationB) => {
          if (locationA.score === locationB.score) {
            return locationA.distance - locationB.distance;
          }
          return locationB.score - locationA.score;
        };

        if (lakesData) {
          lakesByRelevance = lakesData
            .map(setDistance)
            .map(setScore)
            .sort(sortByRelevance);
          dispatch('setLakesByRelevance', lakesByRelevance);
          return lakesByRelevance;
        }
        return null;
      };

      if (useGraphQL) {
        const cache = new InMemoryCache();
        const client = new ApolloClient({ cache, link: new HttpLink({ uri: graphQLEndpoint }) });

        return new Promise((resolve) => {
          client
            .query({
              query: lakeByNameSearchQuery,
              variables: {
                name: state.searchQuery,
                isGarminMap,
              },
            })
            .then((results) => {
              const lakesData = results?.data?.getLakesByName;
              resolve(processLakesData(lakesData));
            });
        });
      }

      // REST API path
      return fetchLakesByName({
        name: state.searchQuery,
        isGarminMap,
      })
        .then((lakesData) => processLakesData(lakesData))
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          return [];
        });
    },
    noResults({ dispatch }) {
      dispatch('setLocationMarker', null);
      dispatch('setNoResultsStates');
    },
    setActiveLake({ commit }, payload) {
      commit('setActiveLake', payload);
    },
    setShowLakeInfo({ commit }, payload) {
      commit('setShowLakeInfo', payload);
    },
    setShowLakeFeatures({ commit }, payload) {
      commit('setShowLakeFeatures', payload);
    },
    setLakesByRelevance({ commit }, payload) {
      commit('setLakesByRelevance', payload);
    },
    setSearchType({ commit }, payload) {
      commit('setSearchType', payload);
    },
  },
};
